import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, MapPin, Building2 } from "lucide-react";
import { api } from "@/lib/api";

export default function SearchBar() {
  const [q, setQ] = useState("");
  const [hotels, setHotels] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.listHotels({ limit: 2000 }).then(setHotels).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return { states: [], cities: [], hotels: [] };
    const stateSet = new Set();
    const citySet = new Map();
    const hotelMatches = [];
    for (const h of hotels) {
      const s = (h.state || "").toLowerCase();
      const c = (h.city || "").toLowerCase();
      const n = (h.name || "").toLowerCase();
      const loc = (h.location || "").toLowerCase();
      if (s.includes(query)) stateSet.add(h.state);
      if (c.includes(query)) citySet.set(`${h.state}::${h.city}`, { state: h.state, city: h.city });
      if (n.includes(query) || loc.includes(query)) hotelMatches.push(h);
    }
    return {
      states: [...stateSet].slice(0, 5),
      cities: [...citySet.values()].slice(0, 5),
      hotels: hotelMatches.slice(0, 8),
    };
  }, [q, hotels]);

  const hasResults = results.states.length || results.cities.length || results.hotels.length;
  const totalHits = results.states.length + results.cities.length + results.hotels.length;

  const go = (path) => {
    setOpen(false);
    setQ("");
    navigate(path);
  };

  return (
    <div ref={boxRef} className="relative w-full" data-testid="search-bar">
      <div className="flex items-center gap-3 border-2 border-[var(--ink)] bg-white px-4 py-3">
        <Search className="w-4 h-4 text-[var(--ink-soft)] shrink-0" />
        <input
          data-testid="search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={loading ? "Loading directory…" : "Search by hotel name, location, city or state…"}
          className="flex-1 bg-transparent outline-none text-sm sm:text-base placeholder:text-[var(--ink-soft)]"
        />
        {q && (
          <button
            data-testid="search-clear"
            onClick={() => { setQ(""); setOpen(false); }}
            className="p-1 hover:bg-[var(--paper-2)]"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        <span className="font-label text-[10px] text-[var(--ink-soft)] hidden sm:inline">
          {hotels.length} indexed
        </span>
      </div>

      {open && q && (
        <div
          data-testid="search-results"
          className="absolute z-30 top-full left-0 right-0 mt-2 border border-[var(--ink)] bg-white shadow-[8px_8px_0_0_var(--ink)] max-h-[70vh] overflow-y-auto"
        >
          {!hasResults ? (
            <div className="p-6 text-sm text-[var(--ink-soft)]">
              No matches for &ldquo;<span className="text-[var(--ink)] font-semibold">{q}</span>&rdquo;.
            </div>
          ) : (
            <>
              <div className="px-4 py-2 border-b border-[var(--line)] font-label text-[10px] text-[var(--ink-soft)] bg-[var(--paper-2)]">
                {totalHits} {totalHits === 1 ? "result" : "results"}
              </div>

              {results.states.length > 0 && (
                <Section label="States">
                  {results.states.map((s) => (
                    <Row
                      key={`s-${s}`}
                      testId={`search-state-${slug(s)}`}
                      icon={<MapPin className="w-3.5 h-3.5" />}
                      title={s}
                      subtitle="State"
                      onClick={() => go(`/state/${encodeURIComponent(s)}`)}
                    />
                  ))}
                </Section>
              )}

              {results.cities.length > 0 && (
                <Section label="Cities">
                  {results.cities.map((c) => (
                    <Row
                      key={`c-${c.state}-${c.city}`}
                      testId={`search-city-${slug(c.city)}`}
                      icon={<MapPin className="w-3.5 h-3.5" />}
                      title={c.city}
                      subtitle={c.state}
                      onClick={() => go(`/state/${encodeURIComponent(c.state)}/city/${encodeURIComponent(c.city)}`)}
                    />
                  ))}
                </Section>
              )}

              {results.hotels.length > 0 && (
                <Section label="Hotels">
                  {results.hotels.map((h) => (
                    <Row
                      key={`h-${h.id}`}
                      testId={`search-hotel-${h.id}`}
                      icon={<Building2 className="w-3.5 h-3.5" />}
                      title={h.name}
                      subtitle={`${h.location ? h.location + " · " : ""}${h.city}, ${h.state}`}
                      onClick={() => go(`/state/${encodeURIComponent(h.state)}/city/${encodeURIComponent(h.city)}`)}
                    />
                  ))}
                </Section>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="px-4 pt-3 pb-1 font-label text-[10px] text-[var(--brand-blue)]">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function Row({ testId, icon, title, subtitle, onClick }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--paper-2)] text-left border-b border-[var(--line)] last:border-0"
    >
      <span className="text-[var(--ink-soft)]">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{title}</div>
        <div className="text-xs text-[var(--ink-soft)] truncate">{subtitle}</div>
      </div>
      <span className="font-label text-[10px] text-[var(--ink-soft)]">Open →</span>
    </button>
  );
}

function slug(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
